/**
 * Reading the conversation preview `GET /v1/conversations` returns, shared by
 * every list that renders one (Home's chats section, the Meeting View's
 * conversations section).
 *
 * Pure on purpose — no React, no auth, no API-route imports — so both callers
 * can unit-test their mapping without pulling in Electron's `app`. The two
 * imports below are types only, and erase.
 */

import type { ReminderItem } from '@renderer/overlay/Sidepanel/hooks/reminder-mapping'
import type { StagedAction } from '@renderer/overlay/Sidepanel/hooks/staged-actions/types'

/**
 * The preview as `ConversationPreviewSchema` puts it on the wire: the assistant
 * text plus the IDs of what that turn produced.
 *
 * Every field is optional because the generated `backend-openapi.d.ts` predates
 * this shape (its `Conversation` has no `preview` at all), so nothing upstream
 * is checking it for us — and because a gateway older than the versioned object
 * still answers with a bare string. See `normalizeConversationPreview`.
 */
export interface ConversationPreviewPayload {
  v?: number
  text?: string
  skillId?: string
  stagedActionIds?: Array<string>
  reminderIds?: Array<string>
}

export interface ConversationPreviewParts {
  /** The latest completed assistant response, as a list row's second line. */
  text?: string
  /** The first skill used by this conversation. */
  skillId?: string
  /** Staged actions that turn created or modified. */
  stagedActionIds: Array<string>
  /** Reminders that turn created or modified. */
  reminderIds: Array<string>
}

function idList(value: unknown): Array<string> {
  return Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string' && id.length > 0) : []
}

/**
 * The preview's three row-facing parts, from either shape it arrives in.
 *
 * A gateway that predates `ConversationPreviewSchema` sends the text as a bare
 * string, so that case is read rather than dropped — it costs one `typeof` and
 * the alternative is a preview-less list against an older backend.
 *
 * Empty text comes back as '' from a chat that hasn't answered yet; it's
 * dropped so the row renders one line rather than a blank second one.
 */
export function normalizeConversationPreview(
  preview: ConversationPreviewPayload | string | null | undefined,
): ConversationPreviewParts {
  if (typeof preview === 'string') {
    return { text: preview.trim() || undefined, stagedActionIds: [], reminderIds: [] }
  }
  return {
    text: preview?.text?.trim() || undefined,
    skillId: typeof preview?.skillId === 'string' && preview.skillId.length > 0 ? preview.skillId : undefined,
    stagedActionIds: idList(preview?.stagedActionIds),
    reminderIds: idList(preview?.reminderIds),
  }
}

/**
 * One thing a conversation's latest turn produced, carrying the whole record —
 * a preview renders these with the same `ViewActionCell` / `ReminderCell` the
 * Ready-for-you and Reminders sections use, and those take the domain object.
 */
export type ConversationArtifact =
  | { kind: 'action'; id: string; action: StagedAction }
  | { kind: 'reminder'; id: string; reminder: ReminderItem }

/**
 * Where the IDs get looked up: whatever list the surface already holds — a
 * meeting's follow-ups, or the panel-wide staged action / reminder caches.
 */
export interface ConversationArtifactSources {
  actions: ReadonlyMap<string, StagedAction>
  reminders: ReadonlyMap<string, ReminderItem>
}

/**
 * ⚠️ An ID with no record behind it is DROPPED, so this can under-report what
 * the preview says a turn produced.
 *
 * That's the cost of rendering the real cells: they're driven by the record,
 * not by the id — there's no `StagedAction`-shaped placeholder to hand
 * `ViewActionCell`. It happens when the preview is ahead of the list the
 * titles come from (a just-created action the panel cache hasn't refetched) or
 * behind it (an action since archived), so it self-corrects on the next fetch.
 */
export function resolveConversationArtifacts(
  parts: Pick<ConversationPreviewParts, 'stagedActionIds' | 'reminderIds'>,
  sources: ConversationArtifactSources,
): Array<ConversationArtifact> {
  const actions = parts.stagedActionIds.flatMap((id) => {
    const action = sources.actions.get(id)
    return action ? [{ kind: 'action' as const, id, action }] : []
  })
  const reminders = parts.reminderIds.flatMap((id) => {
    const reminder = sources.reminders.get(id)
    return reminder ? [{ kind: 'reminder' as const, id, reminder }] : []
  })

  // Actions first: they're the part the user acts on, and a turn that produced
  // both usually produced one reminder to a handful of actions.
  return [...actions, ...reminders]
}
